import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  requestPhoneVerificationCode,
  updatePhone,
  verifyPhoneCode,
  type MyProfileDTO,
} from '@/api/adapters/my-profile';
import { ApiError } from '@/api/error';
import {
  PhoneVerificationCodeSection,
  usePhoneVerification,
  type PhoneVerificationLabels,
} from '@/features/auth/components/PhoneVerificationPanel';
import { MY_PROFILE_QUERY_KEY } from '@/shared/hooks/use-my-profile';
import { toast } from '@/app/store/toast.store';

// PRD 09 §3.3 — phone change via SMS verification, wired through the
// adapter layer. The send → verify state machine lives in
// usePhoneVerification (shared with Signup); this component owns the
// open/close toggle, the new-phone input, the cooldown / re-verify hints,
// and the separate save (commit) step.

const COOLDOWN_SEC = 60; // PRD 11 §10.4 — FE shadows BE's 60s cooldown after a successful send.

const digitsOnly = (value: string): string => value.replace(/\D/g, '');
// Wire form for the BE: strip every non-digit so a hyphenated or plain Korean number sends the same digits.
// The BE accepts only the Korean format (^010\d{8}$); the app ships Korean-only (RULE_PHONE_001), no "+".
const normalizePhone = (value: string): string => digitsOnly(value);

interface PhoneChangePanelProps {
  currentPhone: string | null;
}

export function PhoneChangePanel({ currentPhone }: PhoneChangePanelProps) {
  const { t } = useTranslation('myProfile');
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [serverPhoneError, setServerPhoneError] = useState<string | null>(null);
  const [reVerifyHint, setReVerifyHint] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const newPhoneRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const currentDigits = useMemo(() => digitsOnly(currentPhone ?? ''), [currentPhone]);
  const newPhoneDigits = useMemo(() => digitsOnly(newPhone), [newPhone]);
  // The value sent to the BE — digits only (Korean ^010\d{8}$).
  const newPhoneWire = useMemo(() => normalizePhone(newPhone), [newPhone]);

  // Client-side validation — same checks BE will run, used to disable Send Code early.
  // Phone is a fixed 11-digit Korean number starting with 010 (PRD 09 §4.2 `^010\d{8}$`).
  const phoneValidationKey = useMemo<string | null>(() => {
    if (newPhone === '') return null;
    if (!/^010\d{8}$/.test(newPhoneDigits)) return 'phoneChange.errors.phoneInvalid';
    if (currentDigits && newPhoneDigits === currentDigits) return 'phoneChange.errors.phoneSameAsCurrent';
    return null;
  }, [newPhone, newPhoneDigits, currentDigits]);

  const phoneVerifyLabels: PhoneVerificationLabels = useMemo(
    () => ({
      sendCode: t('phoneChange.actions.sendCode'),
      resend: t('phoneChange.actions.resendCode'),
      codeLabel: t('phoneChange.fields.code'),
      codePlaceholder: t('phoneChange.placeholders.code'),
      verify: t('phoneChange.actions.verify'),
      verified: t('phoneChange.messages.verified'),
      codeInvalid: t('phoneChange.errors.codeMismatch'),
      codeExpired: t('phoneChange.errors.codeExpired'),
      // BE for /me/phone-change doesn't emit ALREADY_VERIFIED; reuse codeMismatch as fallback.
      alreadyVerified: t('phoneChange.errors.codeMismatch'),
      sessionExpired: t('phoneChange.errors.sessionNotFound'),
      serverError: t('phoneChange.errors.generic'),
    }),
    [t],
  );

  const handleSendError = useCallback(
    (err: unknown) => {
      if (!(err instanceof ApiError)) {
        toast.error(t('phoneChange.errors.generic'));
        return;
      }
      switch (err.code) {
        case 'PHONE_INVALID':
          setServerPhoneError(t('phoneChange.errors.phoneInvalid'));
          break;
        case 'PHONE_UNCHANGED':
          setServerPhoneError(t('phoneChange.errors.phoneSameAsCurrent'));
          break;
        case 'PHONE_EMPTY':
          setServerPhoneError(t('phoneChange.errors.phoneEmpty'));
          break;
        case 'PHONE_DUPLICATE':
          setServerPhoneError(t('phoneChange.errors.phoneAlreadyRegistered'));
          break;
        case 'SIGNUP_SMS_RATE_LIMIT':
          // Hook applies the cooldown automatically via err.meta.retryAfterSeconds.
          break;
        case 'SMS_SEND_FAILED':
          toast.error(t('phoneChange.errors.smsSendFailed'));
          break;
        case 'UNAUTHENTICATED':
          // httpClient interceptor handles the logout/redirect; stay quiet.
          break;
        default:
          toast.error(t('phoneChange.errors.generic'));
      }
    },
    [t],
  );

  // Verify-code errors → toast (not inline), matching signup UX.
  const handleVerifyError = useCallback(
    (err: unknown) => {
      if (!(err instanceof ApiError)) {
        toast.error(phoneVerifyLabels.serverError);
        return;
      }
      switch (err.code) {
        case 'OTP_INVALID':
          toast.error(phoneVerifyLabels.codeInvalid);
          break;
        case 'OTP_EXPIRED':
          toast.error(phoneVerifyLabels.codeExpired);
          break;
        case 'SIGNUP_PHONE_UNVERIFIED':
          toast.error(phoneVerifyLabels.sessionExpired);
          break;
        case 'UNAUTHENTICATED':
          // httpClient interceptor handles logout/redirect; stay quiet.
          break;
        default:
          toast.error(phoneVerifyLabels.serverError);
      }
    },
    [phoneVerifyLabels],
  );

  const phoneVerify = usePhoneVerification({
    phone: newPhoneWire,
    cooldownSec: COOLDOWN_SEC,
    onSendCode: requestPhoneVerificationCode,
    onVerifyCode: verifyPhoneCode,
    onVerified: (token) => {
      // token === null → phone edited after a successful verify (auto-reset by the hook).
      // token !== null → fresh verify succeeded.
      setReVerifyHint(token === null);
    },
    onSendError: handleSendError,
    onSendSuccess: () => {
      setReVerifyHint(false);
      // SHCT-360: use success (green) — an `info` toast has no `.toast--info` CSS
      // rule, so it renders transparent. Matches the signup flow for this same message.
      toast.success(t('phoneChange.messages.sendSuccess'));
    },
    onVerifyError: handleVerifyError,
    labels: phoneVerifyLabels,
  });

  const fullReset = useCallback(() => {
    setNewPhone('');
    setPhoneTouched(false);
    setServerPhoneError(null);
    setReVerifyHint(false);
    setIsSaving(false);
    phoneVerify.reset();
  }, [phoneVerify]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      fullReset();
      setIsOpen(false);
    } else {
      setIsOpen(true);
      window.setTimeout(() => newPhoneRef.current?.focus(), 0);
    }
  }, [isOpen, fullReset]);

  const handleNewPhoneChange = useCallback((raw: string) => {
    const next = digitsOnly(raw).slice(0, 11);
    setNewPhone(next);
    setServerPhoneError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (phoneVerify.token === null || isSaving) return;
    setIsSaving(true);
    try {
      const { phone } = await updatePhone(phoneVerify.token);
      // Optimistic + invalidate. The optimistic write keeps the UI snappy; the
      // invalidate guarantees we re-sync against BE in case of normalization
      // differences or concurrent edits from another session.
      queryClient.setQueryData<MyProfileDTO | undefined>(MY_PROFILE_QUERY_KEY, (prev) =>
        prev ? { ...prev, phone } : prev,
      );
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      toast.success(t('phoneChange.messages.saveSuccess'));
      fullReset();
      setIsOpen(false);
      // a11y: return focus to the change-toggle after auto-collapse.
      window.setTimeout(() => toggleRef.current?.focus(), 0);
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'TOKEN_INVALID':
            toast.error(t('phoneChange.errors.tokenInvalid'));
            phoneVerify.reset();
            break;
          case 'TOKEN_EXPIRED':
            toast.error(t('phoneChange.errors.tokenExpired'));
            phoneVerify.reset();
            break;
          case 'UNAUTHENTICATED':
            break;
          default:
            // PRD §8.2 — Maintain verification state on save failure so the user can retry.
            toast.error(t('phoneChange.messages.saveFailure'));
        }
      } else {
        toast.error(t('phoneChange.messages.saveFailure'));
      }
    } finally {
      setIsSaving(false);
    }
  }, [phoneVerify, isSaving, queryClient, t, fullReset]);

  // Avoid stale focus when the panel re-opens after a successful save.
  useEffect(() => {
    if (!isOpen) return;
    newPhoneRef.current?.focus();
  }, [isOpen]);

  // Required-field validation shows only after the field is blurred (PRD 09 §3.3.2,
  // clarification Q54 — validate on leaving the field), so it doesn't fire on open.
  const emptyKey =
    phoneTouched && newPhone === '' ? 'phoneChange.errors.phoneRequired' : null;
  const phoneFieldError =
    serverPhoneError ??
    (emptyKey ? t(emptyKey) : phoneValidationKey ? t(phoneValidationKey) : null);
  const cooldownHint =
    phoneVerify.cooldownRemaining > 0 && !phoneVerify.isVerified
      ? t('phoneChange.errors.cooldown', { seconds: phoneVerify.cooldownRemaining })
      : null;
  const sendDisabled =
    !phoneVerify.canSend || newPhone === '' || phoneValidationKey !== null;

  return (
    <>
      <div className="inline-field">
        <input type="text" value={currentPhone ?? ''} readOnly />
        <button
          ref={toggleRef}
          type="button"
          className="btn btn--secondary btn--sm"
          aria-expanded={isOpen}
          aria-controls="phone-change-panel"
          onClick={handleToggle}
        >
          {isOpen ? t('actions.cancel') : t('actions.change')}
        </button>
      </div>

      {!isOpen ? null : (
        <div className="inline-edit" id="phone-change-panel">
          <div className="form-field">
            <label htmlFor="phone-change-new">{t('phoneChange.fields.newPhone')}</label>
            <div className="inline-field">
              <input
                ref={newPhoneRef}
                id="phone-change-new"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder={t('phoneChange.placeholders.newPhone')}
                value={newPhone}
                aria-invalid={phoneFieldError !== null}
                aria-describedby={
                  phoneFieldError
                    ? 'phone-change-new-error'
                    : cooldownHint || reVerifyHint
                      ? 'phone-change-new-hint'
                      : undefined
                }
                onChange={(e) => handleNewPhoneChange(e.target.value)}
                onBlur={() => setPhoneTouched(true)}
              />
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={sendDisabled}
                aria-busy={phoneVerify.isSending}
                onClick={phoneVerify.send}
              >
                {phoneVerify.sendButtonLabel}
              </button>
            </div>
            {phoneFieldError ? (
              <div id="phone-change-new-error" className="form-field__error" role="alert">
                {phoneFieldError}
              </div>
            ) : cooldownHint ? (
              <div id="phone-change-new-hint" className="form-field__hint">
                {cooldownHint}
              </div>
            ) : reVerifyHint ? (
              <div id="phone-change-new-hint" className="form-field__hint">
                {t('phoneChange.errors.requireReVerify')}
              </div>
            ) : null}
          </div>

          <PhoneVerificationCodeSection
            controller={phoneVerify}
            inputId="phone-change-code"
          />

          <div className="form-actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={!phoneVerify.isVerified || isSaving}
              aria-busy={isSaving}
              onClick={handleSave}
            >
              {t('phoneChange.actions.save')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
